import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import {
    DialogContent,
    Paper,
    Box,
    Typography,
    DialogActions,
    CircularProgress,
    Button,
    Grid,
    SelectChangeEvent,
    MenuItem,
    Dialog,
} from '@mui/material';
import { useStores } from '../hooks/use-stores';
import MaskedTextField from '../components/MaskedMaterialTextField';
import TextField from '../components/TextField';
import Select from '../components/Select';
import DialogTitle from '../components/DialogTitle';
import { useNavigate } from 'react-router-dom';
import { formatDOB } from '../utils/helpers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';

interface Props {
    vfp?: string | null;
    finalize?: boolean;
}

export default observer(function IdentityVerification(props: Props) {
    const { t } = useTranslation();
    const { identityStore, commonStore } = useStores();
    const {
        identityError,
        identityCheckError,
        gettingVerifyStatus,
        eligibilityLoading,
        possessionChecked,
        eligibilityForPrefill,
        identityLoading,
        identityResult,
        finalizeResult,
        finalizing,
        verificationsAttempts,
    } = identityStore;

    const { regionsByCountry } = commonStore;
    const USRegions = regionsByCountry['US'] || [];
    const [dob, setDOB] = useState<Date | null>(null);
    const [last4, setLast4] = useState<string>('');
    const [step, setStep] = useState<number>(1);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [region, setRegion] = useState<string>('');
    const [verificationComplete, setVerificationComplete] = useState<boolean>(false);
    const [wrongDataConfirmed, setWrongDataConfirmed] = useState<boolean>(false);
    const [eligibilityError, setEligibilityError] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        identityStore.getVerifyStatus(true);
        commonStore.loadRegionsByCountry('US');
    }, []);

    useEffect(() => {
        if (gettingVerifyStatus === false) {
            if (!possessionChecked) {
                navigate('/verify_phone');
            } else {
                identityStore.eligibility();
            }
        }
    }, [gettingVerifyStatus]);

    const handleVerifyIdentity = () => {
        if (step === 1) {
            return identityStore.identity(dob, last4);
        }
        if (step === 2) {
            return identityStore.verifyIdentity(firstName, lastName, dob!, last4, city, address, region, postalCode);
        }
    };

    const isFormValid = useMemo(() => {
        if (step === 1) return last4 && last4.length === 4 && dob != null;
        if (step === 2)
            return last4 && last4.length === 4 && dob != null && lastName && firstName && city && address && postalCode && region;
    }, [step, last4, dob, city, region, postalCode, address, firstName, lastName]);

    const actionInProgress = useMemo(() => {
        if (step === 1) return last4 && last4.length === 4 && dob != null;
    }, [step]);

    const handleChangeLast4 = (e: ChangeEvent<HTMLInputElement>) => {
        setLast4(e.target.value);
    };

    const handleChangeFirstName = (e: ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
    };

    const handleChangeLastName = (e: ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
    };
    const handleChangeAddress = (e: ChangeEvent<HTMLInputElement>) => {
        setAddress(e.target.value);
    };
    const handleChangeCity = (e: ChangeEvent<HTMLInputElement>) => {
        setCity(e.target.value);
    };
    const handleChangePostalCode = (e: ChangeEvent<HTMLInputElement>) => {
        setPostalCode(e.target.value);
    };
    const handleChangeRegion = (e: SelectChangeEvent) => {
        setRegion(e.target.value);
    };

    useEffect(() => {
        if (eligibilityLoading === false) {
            if (eligibilityForPrefill === null) setEligibilityError(true);
            else if (eligibilityForPrefill) setStep(1);
            else setStep(2);
        }
    }, [eligibilityLoading, eligibilityForPrefill]);

    useEffect(() => {
        if (finalizing === false) {
            if (finalizeResult?.verified) {
                debugger;
                //show  complete screen if redirection failed.
                setVerificationComplete(true);
                try {
                    if (window.parent)
                        window.parent.location.href = `${process.env.REACT_APP_MY_URL}/wallet/ewallet/add_more_money`;
                    else window.location.href = `${process.env.REACT_APP_MY_URL}/wallet/ewallet/add_more_money`;
                } catch (e) { }
            }
        }
    }, [finalizing, finalizeResult]);

    const handleOpenHelpPage = () => {
        if (window.parent) window.parent.location.href = `${process.env.REACT_APP_MY_URL}/starfish/profile/help`;
        else {
            window.location.href = `${process.env.REACT_APP_MY_URL}/starfish/profile/help`;
        }
    };

    const resetIdentityError = () => {
        identityStore.resetIdentityError();
    };

    useEffect(() => {
        if (identityLoading === false) {
            if (identityResult && identityResult.verified) {
                setStep(2);
                setFirstName(identityResult.firstName || '');
                setLastName(identityResult.lastName || '');
                setDOB(identityResult.dob ? moment(moment.tz(identityResult.dob, 'UTC').startOf('day').format('LL')).startOf('day').toDate() : null);
                setLast4(identityResult.last4 || '');
                setCity(identityResult.city || '');
                setAddress(identityResult.address || '');
                setPostalCode(identityResult.postalCode || '');
                setRegion(identityResult.region || '');
            } else if (identityResult && identityResult?.verified === false) {
                //prefill failed, so user has to manually enter their information
                setStep(2);
                setFirstName('');
                setLastName('');
                setDOB(identityResult.dob ? moment(moment.tz(identityResult.dob, 'UTC').startOf('day').format('LL')).startOf('day').toDate() : null);
                setLast4(identityResult.last4 || '');
                setCity('');
                setAddress('');
                setPostalCode('');
                setRegion('');
            }
        }
    }, [identityLoading, identityResult]);

    return (
        <Box
            sx={{
                color: '#575962',
                height: '100%',
                width: '100%',
                outline: 0,
                display: 'flex',
                alignItems: 'flexStart',
                justifyContent: 'center',
                position: 'fixed',
            }}
        >
            {gettingVerifyStatus === null || gettingVerifyStatus || eligibilityLoading === null || eligibilityLoading ? (
                <CircularProgress />
            ) : (
                <Paper
                    sx={{
                        margin: 0,
                        width: 450,
                        maxWidth: '98%',
                        backgroundColor: 'black',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <DialogTitle>{t('auth:identity.verificationNeeded')}</DialogTitle>
                    <DialogContent
                        sx={(theme) => ({
                            height: '100%',
                            padding: theme.spacing(4),
                            paddingTop: 0,
                            textAlign: 'center',
                        })}
                    >
                        {verificationComplete ? (
                            <>
                                <Typography variant={'subtitle1'}>{t('auth:identity.verificationComplete')}</Typography>
                            </>
                        ) : (
                            <>
                                {step === 1 && (
                                    <>
                                        <Typography>{t('auth:identity.enterDOB')}</Typography>
                                        <Typography>{t('auth:identity.ensureCorrect')}</Typography>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label="Select Date"
                                                value={dob}
                                                openTo={"year"}
                                                views={["year", "month", "day"]}
                                                onChange={(newValue) => {
                                                    // @ts-ignore
                                                    setDOB(
                                                        newValue && // @ts-ignore
                                                        moment.tz(newValue, 'UTC').endOf('day').toDate()
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        id="dob"
                                                        name={'dob'}
                                                        value={dob}
                                                        {...params}
                                                        fullWidth
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        margin={'normal'}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                        <MaskedTextField
                                            getInputRef={last4}
                                            format="####"
                                            fullWidth
                                            id="last4"
                                            name={'last4'}
                                            InputLabelProps={{ shrink: true }}
                                            label={t('auth:identity.last4')}
                                            margin={'normal'}
                                            value={last4}
                                            onChange={handleChangeLast4}
                                        />
                                        {/*{verificationError && (*/}
                                        {/*    <Alert severity="error">{verificationError}</Alert>*/}
                                        {/*)}*/}
                                    </>
                                )}
                                {step === 2 && (
                                    <>
                                        <Typography>
                                            {t('auth:identity.infoFill')}
                                        </Typography>
                                        <Typography>{t('auth:identity.ensureCorrect')}</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.legalFirstName')}
                                                    margin={'normal'}
                                                    value={firstName}
                                                    onChange={handleChangeFirstName}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.legalLastName')}
                                                    margin={'normal'}
                                                    value={lastName}
                                                    onChange={handleChangeLastName}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                                    <DatePicker
                                                        label={t('auth:identity.selectDate')}
                                                        value={dob}
                                                        openTo={"year"}
                                                        views={["year", "month", "day"]}
                                                        onChange={(newValue) => {
                                                            // @ts-ignore
                                                            setDOB(
                                                                newValue &&
                                                                moment.tz(newValue, 'UTC').endOf('day').toDate()
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                fullWidth
                                                                id="dob"
                                                                name={'dob'}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                                margin={'normal'}
                                                            />
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MaskedTextField
                                                    format="####"
                                                    fullWidth
                                                    id="last4"
                                                    name={'last4'}
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.last4')}
                                                    margin={'normal'}
                                                    value={last4}
                                                    onChange={handleChangeLast4}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.address')}
                                                    margin={'normal'}
                                                    value={address}
                                                    onChange={handleChangeAddress}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.city')}
                                                    margin={'none'}
                                                    value={city}
                                                    onChange={handleChangeCity}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                {/* @ts-ignore*/}
                                                <Select fullWidth value={region} onChange={handleChangeRegion}>
                                                    {USRegions.map((x: any) => {
                                                        return (
                                                            <MenuItem key={x.shortCode} value={x.shortCode}>
                                                                {x.name}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    label={t('auth:identity.postalCode')}
                                                    margin={'normal'}
                                                    value={postalCode}
                                                    onChange={handleChangePostalCode}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                        <Button
                            color="primary"
                            size="large"
                            fullWidth
                            variant="contained"
                            onClick={handleVerifyIdentity}
                            disabled={!isFormValid}
                        >
                            {actionInProgress ? <CircularProgress size={26} /> : step === 1 ? 'Continue' : 'Confirm'}
                        </Button>
                    </DialogContent>
                </Paper>
            )}
            {(identityCheckError || identityError || eligibilityError) && (
                <Dialog open={Boolean(identityCheckError) || Boolean(identityError) || eligibilityError}>
                    <DialogTitle>{t('auth:identity.validationFailed')}</DialogTitle>
                    <DialogContent>
                        {identityError || eligibilityError || wrongDataConfirmed || verificationsAttempts > 2 ? (
                            <>
                                {t('auth:identity.contactSupport')}
                            </>
                        ) : (
                            <>
                                {step === 2 && (
                                    <>
                                        <Typography>
                                            <strong>{t('auth:identity.legalFirstName')}:</strong> {firstName}
                                        </Typography>

                                        <Typography>
                                            <strong>{t('auth:identity.legalLastName')}:</strong> {lastName}
                                        </Typography>
                                    </>
                                )}
                                <Typography>
                                    <strong>{t('auth:identity.dateOfBirth')}:</strong> {formatDOB(dob)}
                                </Typography>

                                <Typography>
                                    <strong>{t('auth:identity.last4')}:</strong> {last4}
                                </Typography>
                                {step === 2 && (
                                    <>
                                        <Typography>
                                            <strong>{t('auth:identity.address')}:</strong> {address}
                                        </Typography>

                                        <Typography>
                                            <strong>{t('auth:identity.city')}:</strong> {city}
                                        </Typography>

                                        <Typography>
                                            <strong>{t('auth:identity.region')}:</strong> {region}
                                        </Typography>

                                        <Typography>
                                            <strong>{t('auth:identity.postalCode')}:</strong> {postalCode}
                                        </Typography>
                                    </>
                                )}

                                <Typography sx={{ marginTop: 20 }}>{t('auth:identity.isThisCorrect')}</Typography>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {eligibilityError || wrongDataConfirmed || verificationsAttempts > 2 ? (
                            <Button variant="contained" color="primary" onClick={handleOpenHelpPage}>
                                {t('global:ok')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(e) => {
                                        setWrongDataConfirmed(true);
                                    }}
                                >
                                    {t('auth:identity.yesItsCorrect')}
                                </Button>
                                <Button variant="contained" color="secondary" onClick={resetIdentityError}>
                                    {t('auth:identity.noINeedToChange')}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
});
